import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Instagram, Facebook, Music2, MessageCircle, Users, LucideIconData } from 'lucide-angular';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { PublicCommunityData } from '../../data/home-blocks.model';
import {
  interpolateCommunityText,
  normalizePhoneDigits,
} from '../../../../shared/utils/home-blocks.utils';

@Component({
  selector: 'app-comunidad-block',
  imports: [IconComponent],
  templateUrl: './comunidad-block.component.html',
  styleUrl: './comunidad-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComunidadBlockComponent {
  // El bloque solo se renderiza si el backend lo envía (HomeComponent ya filtra
  // por presencia en el payload). Mantenemos el input nullable como defensa:
  // si llega null, no pintamos nada — RRSS y WhatsApp se ocultan en conjunto.
  readonly data = input<PublicCommunityData | null>(null);
  readonly loading = input(false);

  private readonly router = inject(Router);

  protected readonly whatsappIcon: LucideIconData = MessageCircle;
  protected readonly instagramIcon: LucideIconData = Instagram;
  protected readonly facebookIcon: LucideIconData = Facebook;
  // TODO: replace with TikTok SVG icon — Music2 is a placeholder
  protected readonly tiktokIcon: LucideIconData = Music2;
  protected readonly communityIcon: LucideIconData = Users;

  protected mascotError = signal(false);

  // View-model derivado del payload: aplica la sustitución de marcadores
  // ({jugadores}/{miembros}) y construye el deeplink de WhatsApp. Todo el copy
  // y los enlaces vienen del backoffice; el front solo formatea números e
  // iconos. Devuelve null → el bloque no se pinta.
  protected readonly vm = computed(() => {
    const d = this.data();
    if (!d) {
      return null;
    }
    const digits = normalizePhoneDigits(d.whatsapp.phone);
    return {
      sectionTitle: d.section.title,
      subtitle: interpolateCommunityText(d.section.subtitle, { jugadores: d.players_count }),
      whatsapp: {
        title: d.whatsapp.title,
        description: interpolateCommunityText(d.whatsapp.description, { miembros: d.whatsapp.members_count }),
        cta: d.whatsapp.button_label,
        link: digits ? `https://wa.me/${digits}` : null,
      },
      instagram: d.instagram,
      facebook: d.facebook,
      tiktok: d.tiktok,
      community: d.community,
    };
  });

  protected onMascotError(): void {
    this.mascotError.set(true);
  }

  protected isExternalLink(url: string): boolean {
    return /^https?:\/\//i.test(url);
  }

  protected onChannelClick(event: MouseEvent, url: string): void {
    const isPlainPrimaryClick =
      event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;

    if (!url.startsWith('/') || !isPlainPrimaryClick) {
      return;
    }

    event.preventDefault();
    void this.router.navigateByUrl(url);
  }
}
